import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  DialogContent,
  Typography,
  Box,
  useTheme,
  Divider,
  Chip,
  Button,
  Tooltip,
  IconButton,
  CircularProgress
} from '@mui/material';
import { AutoAwesome as AIIcon, ChevronLeft as BackIcon, InfoOutlined as InfoOutlinedIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import api from '../../../../services/api';
import { colors } from '../../../../design-system/tokens';
import PlatformDialog from '../../../molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../../../molecules/PlatformDialog/PlatformDialogTitle';
import { useBrand } from '../../../../context/BrandContext';
import useBriefingKeywords from '../../../../hooks/useBriefingKeywords';
import { formatDate, formatNumber } from '../../../../utils/contentProcessor';
import {
  isQualitativeAnalysisAiApplied,
  QUALITATIVE_AI_ACCENT
} from '../../../../utils/aiAnalysisStatus';

const FALLBACK_BRAND_NAME = 'a marca';

const namesMatch = (a, b) => {
  const nameA = String(a || '').toLowerCase().trim();
  const nameB = String(b || '').toLowerCase().trim();
  return !!(nameA && nameB && (nameA === nameB || nameA.includes(nameB) || nameB.includes(nameA)));
};

const getArticleTagNames = (article) => (article?.tags || [])
  .map(tag => (typeof tag === 'object' ? tag?.name : null))
  .filter(Boolean);

const getHostname = (url) => {
  if (!url) return null;
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
};

const getSourceReach = (article) => {
  const value = [
    article?.domainInfo?.abrangencia,
    article?.domainInfo?.scope,
    article?.profileInfo?.abrangencia,
    article?.profile?.abrangencia,
    article?.abrangencia,
    article?.coverage,
    article?.scope,
    article?.region,
    article?.reach
  ].find(item => typeof item === 'string' && item.trim());

  return value?.trim() || null;
};

const clampCompetitionScore = (value) => Math.max(-10, Math.min(10, Number(value || 0)));

const getCompetitionScalePercent = (value) => ((clampCompetitionScore(value) + 10) / 20) * 100;

const formatCm2 = (value) => {
  if (!value) return null;
  return `${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} cm²`;
};

const formatBRL = (value) => {
  if (!value) return null;
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
};

const splitSentences = (text) => String(text || '')
  .replace(/\s+/g, ' ')
  .trim()
  .split(/(?<=[.!?])\s+/)
  .map(sentence => sentence.trim())
  .filter(Boolean);

const compactEditorialOpinion = (text) => {
  const sentences = splitSentences(text);
  if (sentences.length <= 2) return text;

  const metricTerms = ['%', 'cm2', 'cm²', 'area editorial', 'espaco editorial', 'ocupa', 'percentual', 'sentimento', 'pageviews', 'audiencia', 'ave', 'valoracao'];
  const editorialSentences = sentences.filter(sentence => {
    const normalizedSentence = sentence
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
    return !metricTerms.some(term => normalizedSentence.includes(term));
  });
  const selected = (editorialSentences.length ? editorialSentences : sentences).slice(0, 2);

  return selected.join(' ');
};

// Copiado do ModalArticleInfoBrandAware para manter a mesma identidade
const getExposureConfig = (exposure, brandName = FALLBACK_BRAND_NAME) => {
  switch (exposure) {
    case 'dedicated':
      return { label: 'Dedicada', color: colors.exposure?.dedicated?.main || '#4caf50' };
    case 'shared':
      return { label: 'Compartilhada', color: colors.exposure?.shared?.main || '#2196f3' };
    case 'mention':
      return { label: 'Citação', color: colors.exposure?.mention?.main || '#9e9e9e' };
    case 'highlight':
      return { label: 'Highlight', color: '#f59e0b' };
    case 'none':
      return { label: 'Sem exposição', color: colors.exposure?.none?.main || '#9e9e9e' };
    default:
      // Fallback para evitar etiquetas vazias nos concorrentes
      return { label: 'Mencionada', color: '#9e9e9e' };
  }
};

const getSentimentConfig = (sentiment) => {
  switch (sentiment) {
    case 'positive':
      return { label: 'Positivo', color: colors.sentiment.positive.main };
    case 'negative':
      return { label: 'Negativo', color: colors.sentiment.negative.main };
    default:
      return { label: 'Neutro', color: colors.sentiment.neutral.main };
  }
};

const KpiaResultsModal = ({ open = false, onClose, article, onRecalculate, embedded = false, embeddedBackLabel = 'Voltar ao artigo', embeddedCompact = false, enableBriefingKeywords = true }) => {
  const [recalculating, setRecalculating] = useState(false);
  const [localArticle, setLocalArticle] = useState(null);
  const [localSmartScore, setLocalSmartScore] = useState(null);

  useEffect(() => {
    setLocalArticle(null);
    setLocalSmartScore(null);
  }, [article?._id]);

  const handleRecalculate = async (e) => {
    e.stopPropagation();
    if (recalculating || !article?._id) return;
    setRecalculating(true);
    try {
      const res = await api.put(`/articles/${article._id}/smart-score`);
      if (res.data?.success) {
        const updatedData = res.data.data;
        if (updatedData) {
          setLocalArticle(updatedData);
        }
        if (updatedData?.smartScore) {
          setLocalSmartScore(updatedData.smartScore);
        }
        if (onRecalculate) onRecalculate(updatedData);
      }
    } catch (err) {
      console.error('[KpiaResultsModal] Erro ao recalcular SmartScore:', err);
    } finally {
      setRecalculating(false);
    }
  };
  const theme = useTheme();
  const { activeBrandData } = useBrand();
  const { keywords: briefingKeywords } = useBriefingKeywords({ enabled: enableBriefingKeywords });
  const isDark = theme.palette.mode === 'dark';

  const occupancy = article?.editorialOccupancy || article?.onlineCentimetragem;
  if (!article || !occupancy) return null;

  const qualitativeAnalysisApplied = isQualitativeAnalysisAiApplied(article);
  const storedEntityBreakdown = Array.isArray(occupancy.entityBreakdown)
    ? occupancy.entityBreakdown.filter(e => e && e.name && e.participationPercent > 0)
    : [];
  const activeBrandName = activeBrandData?.name || storedEntityBreakdown.find(e => e.type === 'brand')?.name || FALLBACK_BRAND_NAME;
  const articleTagNames = getArticleTagNames(article);
  const briefingCompetitorNames = [
    ...new Set((briefingKeywords?.competitor || []).map(item => item?.competitor).filter(Boolean))
  ];
  const missingCompetitorNames = articleTagNames.filter(tagName => {
    const isActiveBrand = namesMatch(tagName, activeBrandName);
    const alreadyDisplayed = storedEntityBreakdown.some(entity => namesMatch(entity.name, tagName));
    const isBriefingCompetitor = briefingCompetitorNames.some(competitorName => namesMatch(competitorName, tagName));
    return !isActiveBrand && !alreadyDisplayed && isBriefingCompetitor;
  });
  const fallbackCompetitorShare = missingCompetitorNames.length > 0
    ? Math.min(0.1, 0.24 / missingCompetitorNames.length)
    : 0;
  const fallbackCompetitors = missingCompetitorNames.map(name => ({
    name,
    type: 'competitor',
    areaCm2: (occupancy.editorialAreaTotalCm2 || occupancy.valueCm2 || 0) * fallbackCompetitorShare,
    participationPercent: fallbackCompetitorShare,
    exposure: 'mention',
    sentiment: 'neutral',
    sentimentSummary: 'Detectado pela classificacao do artigo.'
  }));
  const rawEntityBreakdown = (() => {
    const combined = [...storedEntityBreakdown, ...fallbackCompetitors];
    const total = combined.reduce((sum, entity) => sum + (entity.participationPercent || 0), 0);
    if (total <= 1) return combined;

    return combined.map(entity => ({
      ...entity,
      participationPercent: (entity.participationPercent || 0) / total,
      areaCm2: (entity.areaCm2 || 0) / total
    }));
  })();

  // Normalização editorial: redistribuir "Outros" para a entidade dominante quando ela tem
  // exposição Dedicada. O texto contextual/de mercado de um artigo dedicado faz parte da
  // narrativa da marca protagonista — não deve inflar um bucket genérico.
  // Ex: "Mercedes-AMG lança GT63" → Mercedes 90% + Outros 8% → Mercedes 98%, Porsche 2%
  const entityBreakdown = (() => {
    if (rawEntityBreakdown.length === 0) return rawEntityBreakdown;

    const totalEditorialAreaCm2 = occupancy.editorialAreaTotalCm2 || occupancy.valueCm2 || 0;
    const totalMapped = rawEntityBreakdown.reduce((sum, e) => sum + (e.participationPercent || 0), 0);
    const othersRaw = Math.max(0, 1 - totalMapped); // fração decimal dos "Outros"

    if (othersRaw < 0.005) return rawEntityBreakdown; // nada a redistribuir

    const sorted = [...rawEntityBreakdown].sort((a, b) => b.participationPercent - a.participationPercent);
    const dominant = sorted[0];

    // Redistribuir apenas quando o dominante tem exposição Dedicada
    // (confirma que o conteúdo contextual pertence à sua narrativa)
    if (dominant && dominant.exposure === 'dedicated') {
      return rawEntityBreakdown.map(e => {
        if (e.name === dominant.name) {
          const participationPercent = Math.min(1, e.participationPercent + othersRaw);
          return {
            ...e,
            participationPercent,
            areaCm2: totalEditorialAreaCm2 > 0 ? totalEditorialAreaCm2 * participationPercent : e.areaCm2
          };
        }
        return e;
      }).sort((a, b) => b.participationPercent - a.participationPercent);
    }

    return rawEntityBreakdown.sort((a, b) => b.participationPercent - a.participationPercent);
  })();

  // Gerar parecer comparativo local como fallback se não vier da IA
  const buildFallbackComparative = (entities) => {
    if (!entities || entities.length === 0) return null;
    const sorted = [...entities].sort((a, b) => b.participationPercent - a.participationPercent);

    const exposureLabels = { dedicated: 'exposição dedicada', shared: 'exposição compartilhada', mention: 'citação breve', none: 'sem menção' };
    const sentimentLabels = { positive: 'sentimento positivo', negative: 'sentimento negativo', neutral: 'sentimento neutro' };

    const topPercent = Math.round((sorted[0].participationPercent || 0) * 100);

    // Detectar empate: marcas com percentual dentro de 5 pontos do topo
    const tiedEntities = sorted.filter(e => Math.abs(Math.round((e.participationPercent || 0) * 100) - topPercent) <= 5);
    const restEntities = sorted.filter(e => Math.abs(Math.round((e.participationPercent || 0) * 100) - topPercent) > 5);

    let text;

    if (tiedEntities.length > 1) {
      // Empate ou quase empate
      const names = tiedEntities.map(e => e.name).join(' e ');
      const sharedExposure = tiedEntities.every(e => e.exposure === tiedEntities[0].exposure)
        ? (exposureLabels[tiedEntities[0].exposure] || 'presença semelhante')
        : 'presença equivalente';
      text = `${names} dividem o espaço editorial desta publicação de forma equilibrada, cada uma com ${topPercent}% e ${sharedExposure}.`;
    } else {
      // Dominante claro
      const dominant = sorted[0];
      const otherMappedEntitiesCount = sorted.length - 1;

      if (otherMappedEntitiesCount === 0) {
        // CENÁRIO 1: Apenas UMA marca mapeada (Tom informativo e absoluto)
        if (topPercent >= 80) {
          text = `Esta publicação é focada na marca ${dominant.name}, apresentando ${exposureLabels[dominant.exposure] || 'exposição dedicada'} e ${sentimentLabels[dominant.sentiment] || 'sentimento neutro'}.`;
        } else if (topPercent <= 15) {
          text = `A marca ${dominant.name} é citada brevemente nesta publicação (ocupando ${topPercent}% do espaço), com ${sentimentLabels[dominant.sentiment] || 'sentimento neutro'}.`;
        } else {
          text = `A marca ${dominant.name} aparece nesta publicação com ${exposureLabels[dominant.exposure] || 'presença editorial'} e ${sentimentLabels[dominant.sentiment] || 'sentimento neutro'}, ocupando ${topPercent}% do espaço.`;
        }
      } else {
        // CENÁRIO 2: Disputa entre marcas (Tom comparativo e de liderança)
        text = `${dominant.name} é a marca com maior presença editorial nesta publicação, com ${exposureLabels[dominant.exposure] || 'presença'} e ${sentimentLabels[dominant.sentiment] || 'sentimento neutro'}, ocupando ${topPercent}% do espaço.`;
      }
    }

    if (restEntities.length > 0) {
      const otherTexts = restEntities.map(e =>
        `${e.name} aparece com ${exposureLabels[e.exposure] || 'presença reduzida'} (${Math.round((e.participationPercent || 0) * 100)}%)`
      );
      text += ` ${otherTexts.join('; ')}.`;
    }

    const qualitativeHighlights = sorted
      .map(e => e.sentimentSummary)
      .filter(Boolean)
      .slice(0, 3);
    const strategicHighlights = sorted
      .map(e => e.keyAssociations)
      .filter(Boolean)
      .slice(0, 3);

    if (qualitativeHighlights.length > 0) {
      text += ` Leituras qualitativas: ${qualitativeHighlights.join(' ')}`;
    }
    if (strategicHighlights.length > 0) {
      text += ` Associações estratégicas: ${strategicHighlights.join(' | ')}.`;
    }

    return text;
  };

  // Preferir o parecer rico salvo pela IA; o texto vivo fica como fallback nativo.
  const liveComparativeText = buildFallbackComparative(entityBreakdown);
  const aiComparativeText = occupancy.comparativeAnalysis || null;
  const editorialOpinionText = compactEditorialOpinion(aiComparativeText || liveComparativeText);
  const primaryEntity = entityBreakdown.find(e => e.type === 'brand') || entityBreakdown[0] || {};
  const primaryName = primaryEntity.name || activeBrandName;
  const sourceName = article.siteName || article.profileName || article.author || getHostname(article.url || article.siteUrl) || 'fonte identificada';
  const publicationDate = formatDate(article.date || article.publishedAt || article.createdAt);
  const segment = article.domainInfo?.segmento || article.domainInfo?.category || article.sourceCategory || article.sourceCategories?.[0] || article.section || article.mediaType;
  const reach = getSourceReach(article);
  const dailyAudience = article.audience || article.subscribersNumber || 0;
  const monthlyAudience = article.audienceMonthly > 0
    ? article.audienceMonthly
    : (dailyAudience ? Math.round(dailyAudience * (365 / 12)) : 0);
  const sentimentConfig = getSentimentConfig(primaryEntity.sentiment || article.sentiment);
  const exposureConfig = getExposureConfig(primaryEntity.exposure || article.exposure, primaryName);
  const brandShare = primaryEntity.participationPercent || occupancy.brandParticipationPercent || 0;
  const brandPercent = Math.round(brandShare * 100);
  const totalAreaText = formatCm2(occupancy.editorialAreaTotalCm2 || occupancy.valueCm2);
  const occurrences = primaryEntity.occurrences || occupancy.primaryKeywordOccurrences || 0;
  const spokesperson = occupancy.spokespersonFound || primaryEntity.spokespersonFound || article.spokespersonFound;
  const keyMessages = [
    ...(Array.isArray(occupancy.keyMessagesFound) ? occupancy.keyMessagesFound : []),
    ...(Array.isArray(primaryEntity.keyMessagesFound) ? primaryEntity.keyMessagesFound : [])
  ].filter(Boolean).slice(0, 4);
  const valuationBRL = article.valuation || 0;
  const executiveSummaryText = `Publicação veiculada em ${sourceName}${publicationDate ? `, em ${publicationDate}` : ''}${segment ? `, no segmento ${segment}` : ''}${reach ? `, com abrangência ${reach}` : ''}. A análise identificou exposição ${String(exposureConfig.label || '').toLowerCase()} para ${primaryName}, sentimento ${String(sentimentConfig.label || '').toLowerCase()}${totalAreaText ? `, área editorial de ${totalAreaText}` : ''}${valuationBRL ? ` e AVE estimado de ${formatBRL(valuationBRL)}` : ''}.`;
  const executiveSummaryFacts = [
    occurrences ? `${occurrences} citações` : null,
    brandPercent ? `${brandPercent}% do espaço` : null,
    spokesperson ? `Porta-voz: ${spokesperson}` : null,
    keyMessages.length ? `${keyMessages.length} mensagens-chave` : null,
    reach ? `Abrangência: ${reach}` : null,
    monthlyAudience ? `Audiência mensal: ${formatNumber(monthlyAudience)}` : null
  ].filter(Boolean);

  // Use localSmartScore when available (after recalculation), fallback to article data
  const scoreSourceArticle = localArticle || article;
  const smartScore = (localSmartScore || scoreSourceArticle?.smartScore) || {};
  const hasSmartScore = smartScore.calculatedAt != null;
  const competitorScore = scoreSourceArticle?.competitorScore || {};
  const hasCompetitorScore = competitorScore.calculatedAt != null;
  const isCompetitive = hasCompetitorScore && Array.isArray(competitorScore.competitors) && competitorScore.competitors.length > 0 && entityBreakdown.length > 1;
  const hasBrandEntity = entityBreakdown.some(entity => entity.type === 'brand');
  const isPrimarySmartScoreEntity = (entity) => (
    entity?.type === 'brand' ||
    (!hasBrandEntity && namesMatch(entity?.name, primaryEntity?.name || primaryName))
  );
  const competitionLeftLabel = isCompetitive && competitorScore.competitors.length === 1
    ? competitorScore.competitors[0]?.name || 'Concorrente'
    : 'Concorrentes';
  const competitionRightLabel = primaryName || 'Marca';

  const scoreNum = isCompetitive && competitorScore.brandScore !== undefined 
    ? competitorScore.brandScore 
    : smartScore.score;

  const panelContent = (
    <>
      {embeddedCompact ? (
        <Box
          sx={{
            px: 0,
            pt: 0,
            pb: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
            flexShrink: 0
          }}
        >
          <Button
            size="small"
            startIcon={<BackIcon sx={{ fontSize: 17 }} />}
            onClick={onClose}
            sx={{
              color: 'text.secondary',
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: 999,
              px: 0.5,
              alignSelf: 'flex-start',
              fontSize: '0.75rem',
              '&:hover': {
                color: 'text.primary',
                bgcolor: 'transparent'
              }
            }}
          >
            {embeddedBackLabel}
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1, minWidth: 0 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.06)',
                color: qualitativeAnalysisApplied ? QUALITATIVE_AI_ACCENT : 'text.secondary',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: '0 0 auto'
              }}
            >
              <AIIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.15,
                  color: 'text.primary',
                  fontSize: '0.98rem'
                }}
              >
                Análise Qualitativa
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  color: 'text.secondary',
                  lineHeight: 1.25,
                  mt: 0.25,
                  fontSize: '0.68rem'
                }}
              >
                Espaço editorial e sentimento por marca
              </Typography>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: embedded ? { xs: 'none', md: 'block' } : 'block' }}>
          <PlatformDialogTitle
            title="Análise Qualitativa"
            subtitle="Espaço editorial e sentimento por marca"
            icon={<AIIcon sx={{ color: qualitativeAnalysisApplied ? QUALITATIVE_AI_ACCENT : 'text.secondary' }} />}
            onClose={embedded ? undefined : onClose}
            onBack={embedded ? onClose : undefined}
            hideCloseButton={embedded}
          >
          {qualitativeAnalysisApplied && (
            <Chip
              icon={<AIIcon sx={{ fontSize: '0.72rem !important', color: `${QUALITATIVE_AI_ACCENT} !important` }} />}
              label="IA aplicada"
              size="small"
              sx={{
                height: 20,
                color: QUALITATIVE_AI_ACCENT,
                bgcolor: 'transparent',
                border: 0,
                fontWeight: 760,
                '& .MuiChip-label': { px: 0.65, fontSize: '0.62rem' },
                '& .MuiChip-icon': { ml: 0.55, mr: -0.3 }
              }}
            />
          )}
          {embedded && (
            <Button
              size="small"
              startIcon={<BackIcon />}
              onClick={onClose}
              sx={{
                display: { xs: 'none', md: 'inline-flex' },
                color: 'text.secondary',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: 999,
                px: 1.25,
                '&:hover': {
                  color: 'text.primary',
                  bgcolor: 'action.hover'
                }
              }}
            >
              {embeddedBackLabel}
            </Button>
          )}
          </PlatformDialogTitle>
        </Box>
      )}

      <DialogContent sx={{ p: 0, overflowX: 'hidden' }}>




        <Box sx={{ px: embeddedCompact ? 0 : 3, pt: embeddedCompact ? 1 : 3, pb: embeddedCompact ? 2 : 2.5 }}>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontWeight: 800,
              color: isDark ? 'rgba(255,255,255,0.78)' : 'text.secondary',
              mb: 1,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              fontSize: '0.62rem'
            }}
          >
            Resumo Executivo
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <Typography
              variant="body2"
              sx={{
                color: isDark ? 'rgba(255,255,255,0.76)' : 'text.primary',
                lineHeight: 1.65,
                fontSize: embeddedCompact ? '0.78rem' : '0.9rem'
              }}
            >
              {executiveSummaryText}
            </Typography>

            {executiveSummaryFacts.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {executiveSummaryFacts.map((fact, idx) => (
                  <Chip
                    key={idx}
                    label={fact}
                    size="small"
                    sx={{
                      height: embeddedCompact ? 22 : 24,
                      borderRadius: 999,
                      bgcolor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(15,23,42,0.06)',
                      color: isDark ? 'rgba(255,255,255,0.72)' : 'rgba(15,23,42,0.7)',
                      fontWeight: 700,
                      fontSize: embeddedCompact ? '0.65rem' : '0.7rem',
                      '& .MuiChip-label': { px: 1 }
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Box>

        <Divider />

        {/* Parecer Comparativo */}
        {editorialOpinionText && (
          <Box sx={{ px: embeddedCompact ? 0 : 3, pt: embeddedCompact ? 2 : 3, pb: 2 }}>
            <Typography 
              variant="caption" 
              sx={{ 
                display: 'block',
                fontWeight: 700, 
                color: isDark ? 'rgba(255,255,255,0.7)' : 'text.secondary', 
                mb: 1,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                fontSize: '0.62rem'
              }}
            >
              Parecer Editorial
            </Typography>
            <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.75)' : 'text.primary', lineHeight: 1.7, fontSize: embeddedCompact ? '0.78rem' : '0.9rem' }}>
              {editorialOpinionText}
            </Typography>
          </Box>
        )}

        <Divider />

        {/* SMARTSCORE / DISPUTA COMPETITIVA */}
        {hasSmartScore && (
          <Box sx={{ px: embeddedCompact ? 0 : 3, pt: embeddedCompact ? 2 : 3, pb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  fontWeight: 800,
                  color: isDark ? 'rgba(255,255,255,0.78)' : 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontSize: '0.62rem'
                }}
              >
                {isCompetitive ? 'SmartScore & Disputa Competitiva' : 'Inteligência Editorial (SmartScore)'}
              </Typography>
              <Tooltip 
                placement="top"
                arrow
                title={
                  <Box sx={{ p: 0.5 }}>
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Escala de Status
                    </Typography>
                    {isCompetitive ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '0.65rem' }}><strong style={{color:'#4caf50'}}>8.0 a 10:</strong> Domínio Editorial</Typography>
                        <Typography sx={{ fontSize: '0.65rem' }}><strong style={{color:'#81c784'}}>4.0 a 7.9:</strong> Vantagem Clara</Typography>
                        <Typography sx={{ fontSize: '0.65rem' }}><strong style={{color:'#aed581'}}>1.0 a 3.9:</strong> Vantagem Leve</Typography>
                        <Typography sx={{ fontSize: '0.65rem' }}><strong style={{color:'#9e9e9e'}}>-1.0 a 0.9:</strong> Empate Técnico</Typography>
                        <Typography sx={{ fontSize: '0.65rem' }}><strong style={{color:'#e57373'}}>-3.9 a -1.1:</strong> Desvantagem Leve</Typography>
                        <Typography sx={{ fontSize: '0.65rem' }}><strong style={{color:'#f44336'}}>-7.9 a -4.0:</strong> Desvantagem Significativa</Typography>
                        <Typography sx={{ fontSize: '0.65rem' }}><strong style={{color:'#d32f2f'}}>-10 a -8.0:</strong> Desvantagem Crítica</Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '0.65rem' }}><strong style={{color:'#4caf50'}}>9.1 a 10:</strong> Excelente</Typography>
                        <Typography sx={{ fontSize: '0.65rem' }}><strong style={{color:'#81c784'}}>6.1 a 9.0:</strong> Muito Bom</Typography>
                        <Typography sx={{ fontSize: '0.65rem' }}><strong style={{color:'#aed581'}}>4.1 a 6.0:</strong> Bom</Typography>
                        <Typography sx={{ fontSize: '0.65rem' }}><strong style={{color:'#dce775'}}>2.1 a 4.0:</strong> Regular</Typography>
                        <Typography sx={{ fontSize: '0.65rem' }}><strong style={{color:'#9e9e9e'}}>-2.0 a 2.0:</strong> Alerta</Typography>
                        <Typography sx={{ fontSize: '0.65rem' }}><strong style={{color:'#ffd54f'}}>-4.0 a -2.1:</strong> Irregular</Typography>
                        <Typography sx={{ fontSize: '0.65rem' }}><strong style={{color:'#ffb74d'}}>-6.0 a -4.1:</strong> Ruim</Typography>
                        <Typography sx={{ fontSize: '0.65rem' }}><strong style={{color:'#e57373'}}>-9.0 a -6.1:</strong> Muito Ruim</Typography>
                        <Typography sx={{ fontSize: '0.65rem' }}><strong style={{color:'#d32f2f'}}>-10 a -9.1:</strong> Péssimo</Typography>
                      </Box>
                    )}
                  </Box>
                }
              >
                <InfoOutlinedIcon sx={{ fontSize: 13, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
              {/* Botão de Atualizar SmartScore */}
              <Tooltip title="Atualizar SmartScore" arrow placement="top">
                <IconButton
                  size="small"
                  onClick={handleRecalculate}
                  disabled={recalculating}
                  sx={{
                    p: 0.4,
                    color: 'text.secondary',
                    '&:hover': { color: 'primary.main', bgcolor: 'action.hover' }
                  }}
                >
                  {recalculating
                    ? <CircularProgress size={13} />
                    : <RefreshIcon sx={{ fontSize: 15 }} />
                  }
                </IconButton>
              </Tooltip>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 900, color: (scoreNum || 0) >= 0 ? '#4caf50' : '#f44336', lineHeight: 1 }}>
                {(scoreNum || 0) > 0 ? '+' : ''}{(scoreNum || 0).toFixed(1)}
              </Typography>
              <Box>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: 'text.primary' }}>
                  {isCompetitive ? competitorScore.status : smartScore.status}
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                  {isCompetitive ? `Vantagem calculada via IA sobre ${competitorScore?.competitors?.length || 0} concorrente(s)` : `Impacto ponderado de sentimentos e relevância: ${(smartScore.impactScore || 0).toFixed(1)}`}
                </Typography>
              </Box>
            </Box>

            {/* BARRA ADAPTATIVA */}
            {isCompetitive ? (() => {
              const compScore = competitorScore.score ?? 0;
              const isPositive = compScore > 0;
              const isNegative = compScore < 0;
              const percent = getCompetitionScalePercent(compScore);
              const gradient = 'linear-gradient(90deg, #f44336 0%, #ffb74d 24%, #9e9e9e 50%, #aed581 76%, #4caf50 100%)';
              const dotColor = isNegative ? '#f44336' : (isPositive ? '#4caf50' : '#78909C');

              return (
                <Box sx={{ width: '100%', mt: 1 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', alignItems: 'center', columnGap: 1, mb: 0.5 }}>
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>
                      {competitionLeftLabel}
                    </Typography>
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', textAlign: 'right' }}>
                      {competitionRightLabel}
                    </Typography>
                  </Box>
                  <Box sx={{ position: 'relative', height: 12, mt: 0.5, mx: '6px' }}>
                    <Box sx={{ position: 'absolute', left: 0, right: 0, top: '50%', transform: 'translateY(-50%)', height: 6, borderRadius: 99, overflow: 'hidden', bgcolor: 'rgba(255,255,255,0.05)' }}>
                      <Box sx={{ position: 'absolute', inset: 0, background: gradient, opacity: 0.9 }} />
                    </Box>
                    <Box sx={{
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      width: 2,
                      height: 14,
                      borderRadius: 99,
                      transform: 'translate(-50%, -50%)',
                      bgcolor: 'rgba(255,255,255,0.7)',
                      zIndex: 1
                    }} />
                    <Box sx={{
                      position: 'absolute',
                      left: `${percent}%`,
                      top: '50%',
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      transform: 'translate(-50%, -50%)',
                      bgcolor: dotColor,
                      border: '2px solid #fff',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.4)',
                      zIndex: 2
                    }} />
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', mt: 0.6 }}>
                    <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary' }}>
                      -10
                    </Typography>
                    <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary' }}>
                      0
                    </Typography>
                    <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', textAlign: 'right' }}>
                      +10
                    </Typography>
                  </Box>

                </Box>
              );
            })() : (() => {
              const sScore = smartScore.score || 0;
              const isPositive = sScore > 0;
              const isNegative = sScore < 0;
              const absScore = Math.abs(sScore);
              const percent = Math.min(100, (absScore / 10) * 100);
              const gradient = isNegative 
                ? 'linear-gradient(90deg, #9e9e9e 0%, #ffd54f 30%, #ffb74d 50%, #e57373 75%, #d32f2f 100%)' 
                : 'linear-gradient(90deg, #9e9e9e 0%, #dce775 30%, #aed581 50%, #81c784 75%, #4caf50 100%)';
              const dotColor = isNegative ? '#f44336' : (isPositive ? '#4caf50' : '#78909C');

              return (
                <Box sx={{ width: '100%', mt: 1 }}>
                  <Box sx={{ position: 'relative', height: 12, mt: 1.5, mx: '6px' }}>
                    <Box sx={{ position: 'absolute', left: 0, right: 0, top: '50%', transform: 'translateY(-50%)', height: 6, borderRadius: 99, overflow: 'hidden', bgcolor: 'rgba(255,255,255,0.05)' }}>
                      <Box sx={{ position: 'absolute', inset: 0, background: gradient, opacity: 0.9 }} />
                    </Box>
                    <Box sx={{
                      position: 'absolute',
                      left: `${percent}%`,
                      top: '50%',
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      transform: 'translate(-50%, -50%)',
                      bgcolor: dotColor,
                      border: '2px solid #fff',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.4)',
                      zIndex: 2
                    }} />
                  </Box>

                </Box>
              );
            })()}

            {/* Distribuição Editorial and Qualitativa Moved to Top Block */}
            {entityBreakdown.length > 0 && (
              <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 3 }}>
                
                {/* Distribuição Qualitativa (Share de SmartScore) */}
                {(() => {
                  const entityScoresData = entityBreakdown.map((entity) => {
                    let sNum = null;
                    const isBrand = entity.type === 'brand';
                    const usesPrimarySmartScore = isPrimarySmartScoreEntity(entity);
                    if (usesPrimarySmartScore && hasSmartScore) {
                      sNum = hasCompetitorScore && competitorScore.brandScore !== undefined ? competitorScore.brandScore : smartScore.score;
                    } else if (Array.isArray(competitorScore?.competitors)) {
                      const compData = competitorScore.competitors.find(c => namesMatch(c.name, entity.name));
                      if (compData && compData.score !== undefined) {
                        sNum = compData.score;
                      }
                    }
                    return {
                      name: entity.name,
                      score: sNum,
                      weight: sNum != null ? Math.max(0, sNum + 10) : 0
                    };
                  }).filter(e => e.score != null);

                  const totalWeight = entityScoresData.reduce((sum, e) => sum + e.weight, 0);
                  const activeEntities = entityScoresData.filter(e => e.weight > 0);
                  
                  // Só faz sentido mostrar a barra de Share se houver disputa real (>= 2 entidades com peso)
                  if (activeEntities.length <= 1 || totalWeight <= 0) return null;

                  const SEGMENT_COLORS = isDark
                    ? ['#4caf50', '#2196f3', '#ff9800', '#f44336', '#9c27b0']
                    : ['#2e7d32', '#1565c0', '#ed6c02', '#d32f2f', '#7b1fa2'];

                  return (
                    <Box>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          display: 'block',
                          fontWeight: 700, 
                          color: isDark ? 'rgba(255,255,255,0.7)' : 'text.secondary', 
                          mb: 1.5,
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          fontSize: '0.62rem'
                        }}
                      >
                        Distribuição Qualitativa (Share de SmartScore)
                      </Typography>
                      <Box sx={{ display: 'flex', height: 10, borderRadius: 999, overflow: 'hidden', gap: '2px', mb: 1.5 }}>
                        {entityScoresData.map((eData, idx) => {
                          const pct = Math.round((eData.weight / totalWeight) * 100);
                          if (pct === 0) return null;
                          return (
                            <Box
                              key={idx}
                              title={`${eData.name}: ${pct}% (Score: ${eData.score > 0 ? '+' : ''}${eData.score.toFixed(1)})`}
                              sx={{
                                width: `${pct}%`,
                                bgcolor: SEGMENT_COLORS[idx % SEGMENT_COLORS.length],
                                transition: 'width 0.4s ease',
                                minWidth: 4
                              }}
                            />
                          );
                        })}
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                        {entityScoresData.map((eData, idx) => {
                          const pct = Math.round((eData.weight / totalWeight) * 100);
                          if (pct === 0) return null;
                          return (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: SEGMENT_COLORS[idx % SEGMENT_COLORS.length], flexShrink: 0 }} />
                              <Typography variant="caption" sx={{ fontSize: '0.72rem', color: isDark ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.6)', fontWeight: 700 }}>
                                {eData.name} <span style={{ fontWeight: 400, opacity: 0.75 }}>{pct}%</span>
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  );
                })()}

                {/* Share de Componentes (KPIs) */}
                {(() => {
                  const SEGMENT_COLORS = isDark
                    ? ['#4caf50', '#2196f3', '#ff9800', '#f44336', '#9c27b0']
                    : ['#2e7d32', '#1565c0', '#ed6c02', '#d32f2f', '#7b1fa2'];

                  const kpiMap = new Map();
                  entityBreakdown.forEach(entity => {
                    const isBrand = entity.type === 'brand';
                    const usesPrimarySmartScore = isPrimarySmartScoreEntity(entity);
                    const comps = usesPrimarySmartScore
                      ? (competitorScore?.brandScoreComponents || smartScore?.components || [])
                      : (competitorScore?.competitors?.find(c => namesMatch(c.name, entity.name))?.components || []);
                    comps.forEach(c => {
                      const label = c.label || c.key;
                      if (!label) return;
                      
                      // Normaliza para juntar "Exposição" e "Exposicao" na mesma barra
                      const normalizedKey = label.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
                      
                      // Somente métricas que são particulares de cada entidade fazem sentido comparar o "share"
                      const isEntitySpecific = ['sentimento', 'exposicao', 'titulo', 'foto', 'imagem', 'citacao', 'aspas', 'protagonismo', 'mensagem'].some(k => normalizedKey.includes(k));

                      if (isEntitySpecific && c.enabled !== false && !kpiMap.has(normalizedKey)) {
                        kpiMap.set(normalizedKey, { label: label, key: c.key, normalizedKey });
                      }
                    });
                  });
                  const allKpis = Array.from(kpiMap.values());

                  return allKpis.map((kpi, kpiIdx) => {
                    const kpiData = entityBreakdown.map(entity => {
                      const isBrand = entity.type === 'brand';
                      const usesPrimarySmartScore = isPrimarySmartScoreEntity(entity);
                      const comps = usesPrimarySmartScore
                        ? (competitorScore?.brandScoreComponents || smartScore?.components || [])
                        : (competitorScore?.competitors?.find(c => namesMatch(c.name, entity.name))?.components || []);
                      
                      const cData = comps.find(c => {
                        const l = c.label || c.key;
                        if (!l) return false;
                        return l.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim() === kpi.normalizedKey;
                      });
                      
                      const score = cData?.score != null ? cData.score : null;
                      return {
                        name: entity.name,
                        score: score,
                        weight: score != null ? Math.max(0, score) : 0
                      };
                    }).filter(e => e.score != null);

                    const activeKpiEntities = kpiData.filter(e => e.weight > 0);
                    const totalWeight = kpiData.reduce((sum, e) => sum + e.weight, 0);
                    
                    // Só mostra se houver disputa (>= 2 entidades)
                    if (activeKpiEntities.length <= 1 || totalWeight <= 0) return null;

                    return (
                      <Box key={`kpi-share-${kpiIdx}`}>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            display: 'block',
                            fontWeight: 700, 
                            color: isDark ? 'rgba(255,255,255,0.7)' : 'text.secondary', 
                            mb: 1.5,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            fontSize: '0.62rem'
                          }}
                        >
                          {kpi.label}
                        </Typography>
                        <Box sx={{ display: 'flex', height: 10, borderRadius: 999, overflow: 'hidden', gap: '2px', mb: 1.5 }}>
                          {kpiData.map((eData, idx) => {
                            const pct = Math.round((eData.weight / totalWeight) * 100);
                            if (pct === 0) return null;
                            return (
                              <Box
                                key={idx}
                                title={`${eData.name}: ${pct}% (Score: ${eData.score.toFixed(1)})`}
                                sx={{
                                  width: `${pct}%`,
                                  bgcolor: SEGMENT_COLORS[idx % SEGMENT_COLORS.length],
                                  transition: 'width 0.4s ease',
                                  minWidth: 4
                                }}
                              />
                            );
                          })}
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                          {kpiData.map((eData, idx) => {
                            const pct = Math.round((eData.weight / totalWeight) * 100);
                            if (pct === 0) return null;
                            return (
                              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: SEGMENT_COLORS[idx % SEGMENT_COLORS.length], flexShrink: 0 }} />
                                <Typography variant="caption" sx={{ fontSize: '0.72rem', color: isDark ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.6)', fontWeight: 700 }}>
                                  {eData.name} <span style={{ fontWeight: 400, opacity: 0.75 }}>{pct}%</span>
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  });
                })()}

                {/* Distribuição Editorial */}
                <Box>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      display: 'block',
                      fontWeight: 700, 
                      color: isDark ? 'rgba(255,255,255,0.7)' : 'text.secondary', 
                      mb: 1.5,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      fontSize: '0.62rem'
                    }}
                  >
                    Ocupação Editorial
                  </Typography>
                {(() => {
                  const SEGMENT_COLORS = isDark
                    ? ['#4caf50', '#2196f3', '#ff9800', '#f44336', '#9c27b0']
                    : ['#2e7d32', '#1565c0', '#ed6c02', '#d32f2f', '#7b1fa2'];

                  const totalMapped = entityBreakdown.reduce((sum, e) => sum + Math.round((e.participationPercent || 0) * 100), 0);
                  const othersPercent = Math.max(0, 100 - totalMapped);

                  return (
                    <Box>
                      <Box sx={{ display: 'flex', height: 10, borderRadius: 999, overflow: 'hidden', gap: '2px', mb: 1.5 }}>
                        {entityBreakdown.map((entity, idx) => {
                          const pct = Math.round((entity.participationPercent || 0) * 100);
                          if (pct === 0) return null;
                          return (
                            <Box
                              key={idx}
                              title={`${entity.name}: ${pct}%`}
                              sx={{
                                width: `${pct}%`,
                                bgcolor: SEGMENT_COLORS[idx % SEGMENT_COLORS.length],
                                transition: 'width 0.4s ease',
                                minWidth: 4
                              }}
                            />
                          );
                        })}
                        {othersPercent > 0 && (
                          <Box sx={{ flex: 1, bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' }} />
                        )}
                      </Box>

                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                        {entityBreakdown.map((entity, idx) => {
                          const pct = Math.round((entity.participationPercent || 0) * 100);
                          if (pct === 0) return null;
                          return (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: SEGMENT_COLORS[idx % SEGMENT_COLORS.length], flexShrink: 0 }} />
                              <Typography variant="caption" sx={{ fontSize: '0.72rem', color: isDark ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.6)', fontWeight: 700 }}>
                                {entity.name} <span style={{ fontWeight: 400, opacity: 0.75 }}>{pct}%</span>
                              </Typography>
                            </Box>
                          );
                        })}
                        {othersPercent > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)', flexShrink: 0 }} />
                            <Typography variant="caption" sx={{ fontSize: '0.72rem', color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)', fontWeight: 600 }}>
                              Outros {othersPercent}%
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  );
                })()}
                </Box>
              </Box>
            )}
          </Box>
        )}

        <Divider />

        {/* Entity Breakdown Table/List */}
        <Box sx={{ p: embeddedCompact ? 0 : 3, pt: embeddedCompact ? 2 : 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {entityBreakdown.map((entity, idx) => {
              const percent = Math.round((entity.participationPercent || 0) * 100);
              const isBrand = entity.type === 'brand';
              const usesPrimarySmartScore = isPrimarySmartScoreEntity(entity);
              const neutralBg = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
              const neutralText = isDark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.8)';
              
              const expConfig = entity.exposure ? getExposureConfig(entity.exposure, entity.name) : null;
              const sentConfig = entity.sentiment ? getSentimentConfig(entity.sentiment) : null;

              let entityComponents = [];
              if (usesPrimarySmartScore && smartScore?.components) {
                entityComponents = smartScore.components;
              } else if (Array.isArray(competitorScore?.competitors)) {
                const compData = competitorScore.competitors.find(c => namesMatch(c.name, entity.name));
                if (compData?.components) {
                  entityComponents = compData.components;
                }
              }

              return (
                <Box 
                  key={idx} 
                  sx={{ 
                    py: 2,
                    borderBottom: idx < entityBreakdown.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider'
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          bgcolor: neutralBg,
                          color: neutralText,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.7rem',
                          fontWeight: 800
                        }}
                      >
                        {isBrand ? 'M' : 'C'}
                      </Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: isDark ? '#fff' : '#111827' }}>
                        {entity.name}
                      </Typography>
                    </Box>

                    <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: isDark ? 'rgba(255,255,255,0.9)' : '#111827' }}>
                      {percent}%
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.6)' : 'text.secondary', mr: 'auto', fontWeight: 600 }}>
                      {percent}% · {(entity.areaCm2 || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} cm²
                    </Typography>

                    {/* Highlight — Prioridade Máxima na visualização */}
                    {(entity.inTitle || (article.title && article.title.toLowerCase().includes(entity.name.toLowerCase()))) && (
                      <Chip
                        label="Highlight"
                        size="small"
                        sx={{
                          height: 22,
                          color: '#fff',
                          bgcolor: '#f59e0b',
                          fontWeight: 800,
                          fontSize: '0.65rem',
                          letterSpacing: 0.3,
                          boxShadow: '0 2px 4px rgba(245, 158, 11, 0.2)'
                        }}
                      />
                    )}

                    {expConfig && (
                      <Chip
                        label={expConfig.label}
                        size="small"
                        sx={{
                          height: 22,
                          color: neutralText,
                          bgcolor: neutralBg,
                          fontWeight: 700,
                          fontSize: '0.65rem',
                          letterSpacing: 0.3
                        }}
                      />
                    )}
                    {sentConfig && (
                      <Chip
                        label={sentConfig.label}
                        size="small"
                        sx={{
                          height: 22,
                          color: neutralText,
                          bgcolor: neutralBg,
                          fontWeight: 700,
                          fontSize: '0.65rem',
                          letterSpacing: 0.3
                        }}
                      />
                    )}
                  </Box>

                  {/* SmartScore Bar for Entity */}
                  {(() => {
                    let entityScoreNum = null;
                    if (usesPrimarySmartScore && hasSmartScore) {
                      entityScoreNum = hasCompetitorScore && competitorScore.brandScore !== undefined ? competitorScore.brandScore : smartScore.score;
                    } else if (Array.isArray(competitorScore?.competitors)) {
                      const compData = competitorScore.competitors.find(c => namesMatch(c.name, entity.name));
                      if (compData && compData.score !== undefined) {
                        entityScoreNum = compData.score;
                      }
                    }

                    if (entityScoreNum == null) return null;
                    const isPositive = entityScoreNum > 0;
                    const isNegative = entityScoreNum < 0;
                    const absScore = Math.abs(entityScoreNum);
                    const percent = Math.min(100, (absScore / 10) * 100);
                    
                    const gradient = isNegative 
                      ? 'linear-gradient(90deg, #9e9e9e 0%, #ffd54f 30%, #ffb74d 50%, #e57373 75%, #d32f2f 100%)' 
                      : 'linear-gradient(90deg, #9e9e9e 0%, #dce775 30%, #aed581 50%, #81c784 75%, #4caf50 100%)';
                    const dotColor = isNegative ? '#f44336' : (isPositive ? '#4caf50' : '#78909C');

                    const getEntityStatus = (score) => {
                      const numeric = Number(score);
                      if (numeric >= 9.1) return 'Excelente';
                      if (numeric >= 6.1) return 'Muito Bom';
                      if (numeric >= 4.1) return 'Bom';
                      if (numeric >= 2.1) return 'Regular';
                      if (numeric >= -2) return 'Alerta';
                      if (numeric >= -4) return 'Irregular';
                      if (numeric >= -6) return 'Ruim';
                      if (numeric >= -9) return 'Muito Ruim';
                      return 'Péssimo';
                    };
                    const entityStatus = getEntityStatus(entityScoreNum);

                    return (
                      <Box sx={{ width: '100%', mt: 2, mb: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, alignItems: 'center' }}>
                          <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: isDark ? 'rgba(255,255,255,0.7)' : 'text.secondary', textTransform: 'uppercase' }}>
                            SmartScore: <span style={{ color: entityScoreNum >= 0 ? '#4caf50' : '#f44336' }}>{entityScoreNum > 0 ? '+' : ''}{entityScoreNum.toFixed(1)}</span> <span style={{ opacity: 0.8, textTransform: 'none', marginLeft: 4 }}>({entityStatus})</span>
                          </Typography>
                        </Box>
                        
                        <Box sx={{ position: 'relative', height: 12, mt: 0.5, mx: '6px' }}>
                          <Box sx={{ position: 'absolute', left: 0, right: 0, top: '50%', transform: 'translateY(-50%)', height: 6, borderRadius: 99, overflow: 'hidden', bgcolor: 'rgba(255,255,255,0.05)' }}>
                            <Box sx={{ position: 'absolute', inset: 0, background: gradient, opacity: 0.9 }} />
                          </Box>
                          <Box sx={{
                            position: 'absolute',
                            left: `${Math.max(0, Math.min(100, percent))}%`,
                            top: '50%',
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            transform: 'translate(-50%, -50%)',
                            bgcolor: dotColor,
                            border: '2px solid #fff',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.4)',
                            zIndex: 2
                          }} />
                        </Box>

                      </Box>
                    );
                  })()}

                  {/* Detalhamento de SmartScore Components */}
                  {entityComponents && entityComponents.length > 0 && (() => {
                    let localEntityScoreNum = null;
                    if (usesPrimarySmartScore && hasSmartScore) {
                      localEntityScoreNum = hasCompetitorScore && competitorScore.brandScore !== undefined ? competitorScore.brandScore : smartScore.score;
                    } else if (Array.isArray(competitorScore?.competitors)) {
                      const compData = competitorScore.competitors.find(c => namesMatch(c.name, entity.name));
                      if (compData && compData.score !== undefined) {
                        localEntityScoreNum = compData.score;
                      }
                    }
                    
                    const isNegativeEntity = (localEntityScoreNum || smartScore?.score || 0) < 0;
                    const gradient = isNegativeEntity 
                      ? 'linear-gradient(90deg, #9e9e9e 0%, #ffd54f 30%, #ffb74d 50%, #e57373 75%, #d32f2f 100%)'
                      : 'linear-gradient(90deg, #9e9e9e 0%, #dce775 30%, #aed581 50%, #81c784 75%, #4caf50 100%)';

                    return (
                      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {entityComponents
                          .filter(c => c.enabled !== false)
                          .sort((a, b) => (b.normalizedWeight || 0) - (a.normalizedWeight || 0))
                          .map((comp, cidx) => {
                            const scoreVal = comp.score || 0;
                            const dotColor = isNegativeEntity
                              ? (scoreVal >= 9.1 ? '#d32f2f' : scoreVal >= 6.1 ? '#e57373' : scoreVal >= 4.1 ? '#ffb74d' : scoreVal >= 2.1 ? '#ffd54f' : '#9e9e9e')
                              : (scoreVal >= 9.1 ? '#4caf50' : scoreVal >= 6.1 ? '#81c784' : scoreVal >= 4.1 ? '#aed581' : scoreVal >= 2.1 ? '#dce775' : '#9e9e9e');
                            
                            return (
                              <Box key={cidx} sx={{ display: 'flex', flexDirection: 'column', py: 0.75, borderBottom: '1px solid', borderColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                                     <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: isDark ? 'rgba(255,255,255,0.85)' : 'text.primary' }}>
                                       {comp.label || comp.key}
                                     </Typography>
                                     {comp.rawValue !== undefined && comp.rawValue !== null && comp.rawValue !== '' && (
                                       <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: isDark ? 'rgba(255,255,255,0.5)' : 'text.secondary' }}>
                                         · {(() => {
                                            const val = String(comp.rawValue).toLowerCase().trim();
                                            const key = String(comp.label || comp.key).toLowerCase().trim();
                                            
                                            if (val === 'positive') return 'Positivo';
                                            if (val === 'negative') return 'Negativo';
                                            if (val === 'neutral') return 'Neutro';
                                            
                                            if (val === 'dedicated') return 'Dedicada';
                                            if (val === 'highlight') return 'Destaque';
                                            if (val === 'mention') return 'Menção';
                                            if (val === 'quote') return 'Citação';
                                            
                                            if (key.includes('ocupação') || key.includes('occupancy') || key.includes('participa')) {
                                              const num = Number(comp.rawValue);
                                              if (!isNaN(num)) return `${Math.round(num * 100)}%`;
                                            }
                                            
                                            if (val === 'true') return 'Sim';
                                            if (val === 'false') return 'Não';
                                            
                                            return String(comp.rawValue);
                                         })()}
                                       </Typography>
                                     )}
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: isDark ? 'rgba(255,255,255,0.45)' : 'text.secondary' }}>
                                      {(comp.normalizedWeight || 0).toFixed(0)}%
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 900, color: dotColor, minWidth: 20, textAlign: 'right' }}>
                                      {scoreVal.toFixed(0)}
                                    </Typography>
                                  </Box>
                                </Box>
                                
                                {/* Mini Barra do KPI */}
                                <Box sx={{ position: 'relative', height: 10, mt: 0.4, mx: '6px' }}>
                                  <Box sx={{ position: 'absolute', left: 0, right: 0, top: '50%', transform: 'translateY(-50%)', height: 4, borderRadius: 99, overflow: 'hidden', bgcolor: 'rgba(255,255,255,0.05)' }}>
                                    <Box sx={{ position: 'absolute', inset: 0, background: gradient, opacity: 0.8 }} />
                                  </Box>
                                  <Box sx={{
                                    position: 'absolute',
                                    left: `${Math.max(0, Math.min(100, scoreVal * 10))}%`,
                                    top: '50%',
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    bgcolor: dotColor,
                                    border: '2px solid #fff',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
                                    zIndex: 2
                                  }} />
                                </Box>
                              </Box>
                            );
                          })
                        }
                      </Box>
                    );
                  })()}

                  {/* Justificativa da IA */}
                  {entity.sentimentSummary && (
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 1.5,
                        fontSize: '0.8rem',
                        color: isDark ? 'rgba(255,255,255,0.55)' : 'text.secondary',
                        fontStyle: 'italic',
                        lineHeight: 1.6,
                        borderLeft: '2px solid',
                        borderColor: 'divider',
                        pl: 1.5
                      }}
                    >
                      {entity.sentimentSummary}
                    </Typography>
                  )}
                  {/* Associações Estratégicas (Atletas, Eventos, Ações) */}
                  {entity.keyAssociations && (
                    <Box sx={{ mt: 1.5, pl: 1.5, py: 0.2, borderLeft: '2px solid', borderColor: 'divider', opacity: 0.85 }}>
                      <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: isDark ? 'rgba(255,255,255,0.7)' : 'text.primary', mb: 0.5, textTransform: 'uppercase', fontSize: '0.62rem', letterSpacing: 0.5 }}>
                         Associações Estratégicas
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: isDark ? 'rgba(255,255,255,0.65)' : 'text.secondary', fontWeight: 500, lineHeight: 1.5 }}>
                        {entity.keyAssociations}
                      </Typography>
                    </Box>
                  )}

                  {/* Porta-voz identificado (Soberania Algorítmica) */}
                  {isBrand && occupancy?.spokespersonFound && (
                    <Box sx={{ mt: 1.5, pl: 1.5, py: 0.2, borderLeft: '2px solid', borderColor: '#3b82f6', opacity: 0.85 }}>
                      <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: '#3b82f6', mb: 0.5, textTransform: 'uppercase', fontSize: '0.62rem', letterSpacing: 0.5 }}>
                         Porta-voz Identificado
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: isDark ? 'rgba(255,255,255,0.85)' : '#111827', fontWeight: 700 }}>
                        {occupancy.spokespersonFound}
                      </Typography>
                    </Box>
                  )}

                  {/* Mensagens-chave identificadas (Soberania Algorítmica) */}
                  {isBrand && occupancy?.keyMessagesFound?.length > 0 && (
                    <Box sx={{ mt: 1.5, pl: 1.5, py: 0.2, borderLeft: '2px solid', borderColor: '#8b5cf6', opacity: 0.85 }}>
                      <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: '#8b5cf6', mb: 0.5, textTransform: 'uppercase', fontSize: '0.62rem', letterSpacing: 0.5 }}>
                         Mensagens-Chave Estratégicas
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {occupancy.keyMessagesFound.map((msg, midx) => (
                          <Typography key={midx} variant="body2" sx={{ fontSize: '0.78rem', color: isDark ? 'rgba(255,255,255,0.7)' : 'text.secondary', fontWeight: 500, lineHeight: 1.4 }}>
                             • {msg}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              );
            })}
            
            {entityBreakdown.length === 0 && (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                Nenhuma entidade detalhada encontrada na análise.
              </Typography>
            )}
          </Box>
        </Box>

      </DialogContent>
    </>
  );

  if (embedded) {
    return (
      <Box
        sx={{
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: embeddedCompact ? 'transparent' : 'background.paper'
        }}
      >
        {panelContent}
      </Box>
    );
  }

  return (
    <PlatformDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      {panelContent}
    </PlatformDialog>
  );
};

KpiaResultsModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  article: PropTypes.object,
  onRecalculate: PropTypes.func,
  embedded: PropTypes.bool,
  embeddedBackLabel: PropTypes.string,
  embeddedCompact: PropTypes.bool,
  enableBriefingKeywords: PropTypes.bool
};

export { KpiaResultsModal };
export default KpiaResultsModal;
